import React from "react";
import { Notifier } from "@airbrake/browser";
import {
  GenericErrorIcon,
  Stack,
  Cover,
  Center,
  constants,
} from "@thecb/components";
import styled from "styled-components";
import { DEV_DOMAIN, LOCAL_DOMAIN } from "@/constants/routes";
import {
  isLocalDev,
  getKubernetesCluster,
  getKubernetesNamespaceAndApp,
} from "@/util/domain";

const hostname = window.location.hostname;

const IS_NON_UAT_PROD_ENV =
  [DEV_DOMAIN, LOCAL_DOMAIN].some((url) => hostname.includes(url)) ||
  (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "uat");

const KUBERNETES_ENVIRONMENT = getKubernetesCluster(hostname);
const KUBERNETES_NAMESPACE = getKubernetesNamespaceAndApp(hostname);

const ErrorBoundaryTitle = styled.h1`
  font-size: 2rem;
  line-height: 1.5;
  color: ${constants.colors.FIREFLY_GREY};
  font-weight: ${constants.fontWeights.FONT_WEIGHT_SEMIBOLD};
  text-align: center;
  font-family:
    Public Sans,
    Merriweather,
    Tahoma,
    Sans-Serif;
  padding-top: 1rem;
`;

const ErrorBoundaryDetail = styled.p`
  font-size: 1.25rem;
  line-height: 1.5;
  color: ${constants.colors.FIREFLY_GREY};
  font-weight: ${constants.fontWeights.FONT_WEIGHT_REGULAR};
  text-align: center;
  font-family:
    Public Sans,
    Merriweather,
    Tahoma,
    Sans-Serif;
  padding-bottom: 1rem;
`;

export const BoundaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6rem;
`;

/*
  As of 06/23, ErrorBoundary components must still be written in Class Component style
*/

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };

    if (!isLocalDev(hostname)) {
      this.airbrake = new Notifier({
        projectId: 242638,
        projectKey: "b341a7bb115259b332afd77ff4199603",
        environment: KUBERNETES_ENVIRONMENT,
        namespace: KUBERNETES_NAMESPACE,
      });
      this.airbrake.addFilter((notice) => {
        if (IS_NON_UAT_PROD_ENV) return null;

        notice.context.environment = KUBERNETES_ENVIRONMENT;
        return notice;
      });
    } else {
      this.airbrake = {
        notify: (_) => null,
      };
    }
  }

  componentDidCatch(error, info) {
    this.setState({
      hasError: true,
    });
    this.airbrake.notify({
      error,
      context: {
        component: "<ErrorBoundary />",
      },
      params: {
        info,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <BoundaryContainer>
          <Cover singleChild>
            <Center intrinsic>
              <Stack padding="0" childGap="0.5rem">
                <GenericErrorIcon />
                <ErrorBoundaryTitle>Something Went Wrong</ErrorBoundaryTitle>
                <ErrorBoundaryDetail>
                  Please return home and try again.
                </ErrorBoundaryDetail>
              </Stack>
            </Center>
          </Cover>
        </BoundaryContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
